import { Box, Button, Stack, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useCreateMerchantMutation } from "Api/Mutations/Merchants/useCreateMerchantMutation";
import { merchantsListKeys } from "Api/Queries/Merchants/useGetMerchantsQuery";
import { StatusIndicator } from "Components/Hunter/Merchants/NewRegistration/StatusIndicator";
import { useAvailableAresData } from "Components/Hunter/Merchants/hooks/useAresDataWithAvailability";
import { useCreatePartyWithCurrentUserRelation } from "Hooks/useCreatePartyWithCurrentUserRelation";
import { PrimaryButton } from "Components/Shared/Buttons/PrimaryButton";
import { FormSection } from "Components/Shared/FormSection";
import { CheckboxInput } from "Components/Shared/Inputs/Form/CheckboxInput";
import { Dropdown } from "Components/Shared/Inputs/Form/Dropdown";
import { PartyLicenseSelector } from "Components/Shared/Inputs/Form/PartyLicenseSelector/PartyLicenseSelector";
import { PrimaryPaper } from "Components/Shared/PrimaryPaper";
import { StyledForm } from "Components/Users/UserDetailPageContent/Components/StyledForm";
import { HunterAutocomplete } from "Components/Shared/Inputs/HunterAutocomplete";
import { HunterList } from "Components/Shared/Inputs/HunterList/HunterList";
import {
  type AddressDto,
  type HunterSearchDto,
  type MerchantDetailDto,
  type PartyLicenseDto,
  type PartyType,
  type Region,
  RelationType,
} from "Infrastructure/Api/Api";
import { AccessRoleCodes } from "Models/AccessRoleCodes";
import { useRegionList } from "Hooks/Options/useRegionList";
import { isLicenseActive } from "Utils/LicenseUtils";
import { appUrl } from "Utils/UrlUtils";
import { type Path, useForm } from "react-hook-form";
import { useNavigate } from "Navigation/useNavigate";
import { toast } from "sonner";
import { Resources, useResource } from "Translations/Resources";
import { useState } from "react";
import { LicenseShareCard } from "Components/Shared/Inputs/HunterList/LicenseShareCard";
import { FormInput } from "Components/Shared/Inputs/Form/FormInput";
import { MERCHANT_COMPLETE_REGISTRATION_PAGE_STEPS_IDS } from "Components/Hunter/Merchants/CompleteRegistration/steps";

const COMPONENT_RESOURCES = Resources.Hunter.Merchants.NewRegistration;
const MIN_COMMISSION_TO_KEEP = 1; //main hunter must keep at least 1% for himself

type MerchantRegistrationModel = {
  companyNumber: string;
  type: PartyType;
  companyName: string;
  address: AddressDto;
  licensePublicID: string;
};

type LicensePublicID = string;
type ExtendedFormModel = MerchantRegistrationModel & {
  meetingConfirmation: boolean;
  // No preselected market — CZ and SK company numbers are both 8 digits, so a wrong preselected
  // register could resolve a different country's company with the same number. The hunter must
  // pick explicitly; the company-number input stays disabled until then.
  region?: Region;
  partyLicenseFees: Record<LicensePublicID, number>;
};

type Props = { licenses: Array<PartyLicenseDto> };

export const MerchantNewRegistrationForm = ({ licenses }: Props) => {
  const [addedHunters, setAddedHunters] = useState<Array<HunterSearchDto>>([]);
  const [isAddingHunter, setIsAddingHunter] = useState(false);
  const [lastUpdatedHunterLicenseID, setLastUpdatedHunterLicenseID] =
    useState<LicensePublicID>("");
  const { t } = useResource();
  const navigate = useNavigate();

  const { codeListRegions } = useRegionList();

  const getDefaultLicense = (region: Region) =>
    licenses.find(
      (license) => license.region === region && isLicenseActive(license),
    );

  const { handleSubmit, control, watch, setValue, unregister } =
    useForm<ExtendedFormModel>({
      defaultValues: {
        companyNumber: "",
        partyLicenseFees: {}, //only other hunters, current hunter is not included
      },
    });
  // isPending must gate submit too: the button used to re-enable once the Party was created while
  // the Merchant POST was still running, and a second click on a slow prod made two Merchants.
  const { mutate: createMerchant, isPending: isCreatingMerchant } =
    useCreateMerchantMutation();
  const queryClient = useQueryClient();

  const licensePublicID = watch("licensePublicID");
  const partyLicenseFees = watch("partyLicenseFees");
  const companyNumber = watch("companyNumber");
  const hasConfirmedMeeting = watch("meetingConfirmation");
  // The selected market drives everything below: which business register is queried (CZ → ARES,
  // SK → RPO), the availability check scope, which licenses can be assigned, and the region
  // persisted on the created Party. Commission can only be shared within one market.
  const region = watch("region");

  const regionLicenses = region
    ? licenses.filter((license) => license.region === region)
    : [];
  const hasActiveLicenseForRegion = regionLicenses.some(isLicenseActive);

  const {
    postPartyWithCurrentUserRelation,
    isPending: isCreatingPartyWithRelation,
  } = useCreatePartyWithCurrentUserRelation({
    relationType: RelationType.HuntersMerchant,
    onSuccess: (publicPartyID) => {
      createMerchant(
        { licensePublicID, publicPartyID, partyLicenseFees },
        {
          onSuccess: (data: MerchantDetailDto) => {
            queryClient.invalidateQueries({
              queryKey: merchantsListKeys.all,
            });
            toast.success(t(COMPONENT_RESOURCES.StartRegistrationSuccess));
            navigate(
              `${appUrl("merchant-complete-registration")}/${data.publicID}?activeStep=${MERCHANT_COMPLETE_REGISTRATION_PAGE_STEPS_IDS[0]}`,
            );
          },
          onError: () => {
            toast.error(t(COMPONENT_RESOURCES.StartRegistrationError));
          },
        },
      );
    },
    onError: () => {
      toast.error(t(COMPONENT_RESOURCES.CreateMerchantError));
    },
  });

  const {
    aresData,
    isInvalid,
    isAvailable,
    isLoading: isLoadingCompany,
    reset: resetAresData,
  } = useAvailableAresData(companyNumber, region, AccessRoleCodes.Merchant);

  // Switching the market invalidates everything already entered — the register result belongs to
  // the other country's register and licenses/share hunters are market-scoped.
  const handleRegionChange = (newRegion: Region) => {
    resetAresData();
    setValue("companyNumber", "");
    setValue("licensePublicID", getDefaultLicense(newRegion)?.publicID ?? "");
    setValue("partyLicenseFees", {});
    setAddedHunters([]);
    setIsAddingHunter(false);
  };

  const onSubmit = () => {
    if (aresData && region) {
      // The register payload carries no region — stamp the selected market onto the created Party.
      postPartyWithCurrentUserRelation({ ...aresData, region });
    }
  };

  const isCompanyAvailable = isAvailable && !!aresData;
  const isLoading =
    isLoadingCompany || isCreatingPartyWithRelation || isCreatingMerchant;
  const isError = !!aresData && !isAvailable;

  const totalCommission = partyLicenseFees
    ? Object.values(partyLicenseFees).reduce(
        (total, commission) => total + commission,
        0,
      )
    : 0;

  const handleHuntersChange = (hunters: Array<HunterSearchDto>) => {
    setAddedHunters(hunters);
    setIsAddingHunter(false);

    hunters.forEach(({ licensePublicID }) => {
      const currentValue = watch(
        `partyLicenseFees.${licensePublicID}` as Path<ExtendedFormModel>,
      );
      if (currentValue === undefined) {
        setValue(
          `partyLicenseFees.${licensePublicID}` as Path<ExtendedFormModel>,
          0,
        );
      }
    });
  };

  const handleRemoveHunter = (licensePublicID: string) => {
    setAddedHunters(
      addedHunters.filter((h) => h.licensePublicID !== licensePublicID),
    );
    unregister(
      `partyLicenseFees.${licensePublicID}` as Path<ExtendedFormModel>,
    );
  };

  const formattedCompanyName =
    (companyNumber.length === 8 && aresData?.companyName) || null;

  const mainHunterCommission = 100 - totalCommission;
  const isTotalCommissionOverLimit =
    totalCommission + MIN_COMMISSION_TO_KEEP > 100;
  const formattedMainHunterCommission = isTotalCommissionOverLimit
    ? `< ${MIN_COMMISSION_TO_KEEP}`
    : Math.min(
        100,
        Math.max(mainHunterCommission, MIN_COMMISSION_TO_KEEP),
      ).toFixed(2);

  const isAnyCommissionNegative = partyLicenseFees
    ? Object.values(partyLicenseFees).some((commission) => commission < 0)
    : false;
  const isCommissionInvalid =
    isTotalCommissionOverLimit || isAnyCommissionNegative;

  const hunterLicenseIDExceedingLimit = isCommissionInvalid
    ? lastUpdatedHunterLicenseID
    : undefined;

  const isSubmitDisabled =
    !hasConfirmedMeeting ||
    isLoading ||
    !region ||
    !aresData ||
    isError ||
    !licensePublicID ||
    isCommissionInvalid;

  return (
    <StyledForm onSubmit={handleSubmit(onSubmit)}>
      <PrimaryPaper>
        <Stack>
          <FormSection>
            <Dropdown
              codeList={codeListRegions}
              control={control}
              name="region"
              label={t(COMPONENT_RESOURCES.Form.Fields.Region)}
              onChange={(value) => handleRegionChange(value as Region)}
            />
            {region && !hasActiveLicenseForRegion && (
              <Typography color="error" variant="body2">
                {t(COMPONENT_RESOURCES.Form.NoLicenseForRegion)}
              </Typography>
            )}
            <FormInput
              name="companyNumber"
              control={control}
              label={t(COMPONENT_RESOURCES.Form.Fields.CompanyNumber)}
              disabled={isLoadingCompany || !region}
              error={isError}
            />
            <Box display="flex" alignItems="center">
              <StatusIndicator
                isInvalid={isInvalid}
                company={formattedCompanyName}
                isLoadingCompany={isLoadingCompany}
                isCompanyAvailable={isCompanyAvailable}
              />
            </Box>
          </FormSection>
          {isCompanyAvailable && (
            <>
              <FormSection legend={t(COMPONENT_RESOURCES.Form.Legend.License)}>
                <PartyLicenseSelector
                  control={control}
                  name="licensePublicID"
                  aria-label={t(COMPONENT_RESOURCES.Form.Legend.License)}
                  licenses={regionLicenses}
                />
              </FormSection>
              <FormSection
                legend={t(COMPONENT_RESOURCES.Form.Legend.CommissionSharing)}
              >
                <Stack gap={2}>
                  <LicenseShareCard
                    isPrimary
                    hasError={isCommissionInvalid}
                    licenseNo={
                      licenses.find(
                        (license) => license.publicID === licensePublicID,
                      )?.licenseNo || ""
                    }
                    commission={formattedMainHunterCommission}
                    companyName={t(
                      Resources.Components.Shared.HunterList.MainHunterCard
                        .YourCommission,
                    )}
                  />
                  <HunterList
                    hunters={addedHunters}
                    onRemoveHunter={handleRemoveHunter}
                    control={control}
                    hunterLicenseIDExceedingLimit={
                      hunterLicenseIDExceedingLimit
                    }
                    onCommissionUpdate={setLastUpdatedHunterLicenseID}
                  />
                  {isAddingHunter ? (
                    <Box mt={2}>
                      <HunterAutocomplete
                        selectedHunters={addedHunters}
                        onHuntersChange={handleHuntersChange}
                        disabled={isLoading}
                        region={region}
                      />
                    </Box>
                  ) : (
                    <Button
                      color="primary"
                      variant="text"
                      onClick={() => setIsAddingHunter(true)}
                      sx={{ textTransform: "none", justifySelf: "center" }}
                    >
                      {t(
                        COMPONENT_RESOURCES.Form.Fields
                          .ShareCommissionWithHuntersButton,
                      )}
                    </Button>
                  )}
                </Stack>
              </FormSection>
            </>
          )}
          <Box my={4}>
            <CheckboxInput
              control={control}
              name="meetingConfirmation"
              label={t(COMPONENT_RESOURCES.Form.Fields.ConfirmationLabel)}
            />
          </Box>
          <PrimaryButton
            fullWidth
            type="submit"
            disabled={isSubmitDisabled}
            isLoading={isCreatingPartyWithRelation || isCreatingMerchant}
            variant="contained"
            color="primary"
          >
            {t(COMPONENT_RESOURCES.StartRegistrationButton)}
          </PrimaryButton>
        </Stack>
      </PrimaryPaper>
    </StyledForm>
  );
};
